<template>
    <IonLabel class="hint">
        Hier siehst du, in welchen Monaten du bereits Urlaub geplant hast &ndash; und wie viel Restanspruch für {{month.formatKapitelYear(year)}} noch unverplant ist.</IonLabel>
    <Skeleton v-if="initialDataLoader.pending.value" :items="[`${APEX_SKELETON_WITH_SUMMARY}`,'block']"/>
    <template v-else>
        <div class="boxed margin-bottom-regular">
            <ApexChartValueLine :values="monthRanger.selectedRenderSet.value
                ? [{
                    label: 'geplant im ' + monthRanger.selectedCalendarMonth.value.format('MMMM'),
                    value: monthRanger.selectedRenderSet.value.planned,
                    formatter: value => formatter(value as number)
                }]
                : []
            " />
            <ApexChartWrapper :options="apexOptions"></ApexChartWrapper>
        </div>
        <ApexChartSummaryLine
            :number="cumulativeRenderSet.unplanned?.toString() ?? '-'"
            :text="`${ifIsSet(cumulativeRenderSet.unplanned, formatterUnitOnly, '')} Urlaub noch unverplant, ${formatter(cumulativeRenderSet.planned)} bereits geplant in ${formatKapitelYear(year)} (Stand: ${date.formatKapitelDate(date.endOfMonth(cumulativeRenderSet.month))}).`"
        />
<!--        <RichResponseListItem-->
<!--            v-if="[12,1,2].includes(date.getMonthFromKapitelDate(cumulativeRenderSet.month))"-->
<!--            class="margin-top-regular"-->
<!--            :icon-type="'icon'"-->
<!--            :icon="'/icons/info.svg'"-->
<!--            icon-color="primary"-->
<!--            :text="'Resturlaub aus dem Vorjahr muss bis zum 31.3. des Folgejahres aufgebraucht werden'"-->
<!--        />-->
    </template>
</template>

<script setup lang="ts">
import {computed, PropType, Ref, ref} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";

import {kapitelMonthString} from '@/graphql/kapitelTypes';
import {toMap} from "@/helper/array";
import {APEX_SKELETON_WITH_SUMMARY, generateApexOptions} from "@/helper/apexchart";
import month, {formatKapitelYear} from "@/helper/datetime/month";
import {pluralize, pluralizeUnitOnly} from "@/helper/amountFormatter";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import {IonLabel} from "@ionic/vue";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import ApexChartSummaryLine from "@/views/components/ApexChartSummaryLine.vue";
import {fetchEmployeeMonthSummaries} from "@/helper/employeeMonthSummary";
import {EmployeeMonthSummary, EmployeePlannedAbsenceConstraints} from "@/graphql/generated/graphql";
import {fetchEmployeePlannedAbsenceConstraints} from "@/helper/employeePlannedAbsenceConstraints";
import {ifIsSet} from "@/helper/isset";
import {getMostRecentPublishedMonth, MonthKPIApexMonthRangerFactory} from "@/helper/monthKPI/monthKPI";
import date from "@/helper/datetime/date";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";

// props
const props = defineProps({
    selectedMonth: {
        type: String as PropType<kapitelMonthString>,
        required: true,
    }
});
const year = computed(() => month.getKapitelYearStringFromKapitelMonthString(props.selectedMonth))

const mostRecentPublishedMonth = getMostRecentPublishedMonth()
if (month.getKapitelYearStringFromKapitelMonthString(mostRecentPublishedMonth) !== year.value) {
    month.lastMonthOfYear(year.value)
}

// loader
const initialDataLoader = new DataLoader();

const formatter = (v: number) => pluralize(v,'Tag', 'Tage')
const formatterUnitOnly = (v: number) => pluralizeUnitOnly(v,'Tag', 'Tage')

/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    min: month.firstMonthOfYear(year.value),
    max: month.lastMonthOfYear(year.value),
    selected: props.selectedMonth,
    renderer: (calendarMonth: CalendarMonth) => {
        return {
            planned: monthSummaryMap.value.get(calendarMonth.getId())?.cntVacationDays || 0
        }
    }
})

/**
 * raw data
 */

const monthSummaries = ref(<EmployeeMonthSummary[]>[])
const monthSummaryMap = computed(() => toMap(monthSummaries.value, (e) => e.month))
initialDataLoader.add(async () => monthSummaries.value = await fetchEmployeeMonthSummaries(monthRanger.min, monthRanger.max))

const mostRecentPlannedAbsenceConstraints : Ref<EmployeePlannedAbsenceConstraints | undefined> = ref()
initialDataLoader.add(async () => mostRecentPlannedAbsenceConstraints.value = await fetchEmployeePlannedAbsenceConstraints(mostRecentPublishedMonth))


/**
 * render
 */

const apexOptions = computed(() => {
    const monthLabels = monthRanger.getMonthLabels()

    return generateApexOptions(
        monthRanger,
        {
            series: [{
                name: "Urlaubstage",
                data: monthRanger.allMonthsRenderSets.map(rs => rs.value.planned),
            }],
            chart: { type: 'bar' },
            yaxis: { labels: { formatter: (value: number) => formatter(value) } }
        },
    )
})

const cumulativeRenderSet = computed(() => {
    return {
        month: mostRecentPublishedMonth,
        planned: mostRecentPlannedAbsenceConstraints.value?.plannedOrUsedVacation || 0,
        unplanned: mostRecentPlannedAbsenceConstraints.value?.remainingUnplannedVacation ?? null
    }
})


/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>

<style lang="scss" scoped>
</style>
